import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { insidePages } from '../data/insidePages';
import { OFFER_ANCHOR, PRODUCT } from '../data/constants';
export function LookInside() {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(0);
  const scrollTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(index, insidePages.length - 1));
    track.scrollTo({
      left: clamped * track.clientWidth,
      behavior: 'smooth'
    });
    setActive(clamped);
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const next = Math.round(track.scrollLeft / track.clientWidth);
      setActive(Math.max(0, Math.min(next, insidePages.length - 1)));
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);
  const arrowClass = 'absolute top-1/2 z-10 -translate-y-1/2 rounded-full border-2 border-white bg-navy p-2 text-white shadow-lg transition-[background-color,opacity] duration-150 ease-out hover:bg-tech disabled:pointer-events-none disabled:opacity-0 sm:p-3';
  return <section className="border-y-4 border-tech bg-white py-5 sm:py-7" data-id="element-92" data-mp="f1hjwomry2jo">
      <div className="mx-auto max-w-5xl px-4 sm:px-6" data-id="element-93" data-mp="3nhb0f3h3ve0q">
        <h2 className="text-center text-3xl leading-none text-navy sm:text-5xl" data-id="element-94" data-mp="3ni3vv8am6h3k">
          APERÇU <span className="text-tech" data-id="element-95" data-mp="3ijsm0xkbk6lc">DU CONTENU</span>
        </h2>
        <p className="mt-1 text-center font-display text-sm font-semibold uppercase leading-tight text-tech sm:text-lg" data-id="element-96" data-mp="12hw1j735rok">
          Découvrez l’intérieur de {PRODUCT}
        </p>
        <p className="mx-auto mt-1 max-w-3xl text-center text-sm leading-snug text-steel sm:text-base" data-id="element-97" data-mp="34r30eu8pd3or">
          Système de conduction, lecture complète pas à pas, comparaisons de rythmes, territoires
          sur 12 dérivations et cas pédagogiques corrigés inclus dans le système complet.
          Tracés pédagogiques.
        </p>

        <div className="relative mt-3" data-id="element-98" data-mp="18mwn0f31ggi8">
          <div ref={trackRef} className="flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="Carrousel des pages intérieures" data-id="element-99" data-mp="18lty2cpa0o4y">
            {insidePages.map((page, i) => <div key={`page-${i}`} className="w-full shrink-0 snap-center px-0.5" role="group" aria-roledescription="slide" aria-label={`Page ${i + 1} sur ${insidePages.length}`} data-id="element-100" data-mp="pp2es39qi90w" data-mpi={i}>
                <a href={OFFER_ANCHOR} aria-label={`${page.alt} — voir l’offre`} className="mx-auto block max-w-md overflow-hidden rounded-xl border border-metal bg-white shadow-xl" data-id="element-101" data-mp="yno0266lc25d">
                  <img src={page.src} alt={page.alt} width={1792} height={2240} loading={i === 0 ? 'eager' : 'lazy'} className="block aspect-[4/5] h-auto w-full object-contain" data-id="element-102" data-mp="2k7t0jk9uq66t" />
                </a>
                <p className="mx-auto mt-1.5 max-w-md text-center font-display text-xs font-bold uppercase tracking-wide text-navy sm:text-sm" data-id="element-103" data-mp="26fd452bgh6ev">
                  {page.alt}
                </p>
              </div>)}
          </div>

          <button type="button" onClick={() => scrollTo(active - 1)} disabled={active === 0} aria-label="Page précédente" className={`${arrowClass} left-1 sm:left-4`} data-id="element-104" data-mp="1rjnf0vaz4d5x">
            <ChevronLeftIcon className="h-5 w-5" strokeWidth={3} data-id="element-105" data-mp="3lxocbbqvks3w" />
          </button>
          <button type="button" onClick={() => scrollTo(active + 1)} disabled={active === insidePages.length - 1} aria-label="Page suivante" className={`${arrowClass} right-1 sm:right-4`} data-id="element-106" data-mp="3hxxp1i0k4u44">
            <ChevronRightIcon className="h-5 w-5" strokeWidth={3} data-id="element-107" data-mp="3fzegf21qydf8" />
          </button>
        </div>

        <div className="mt-2.5 flex items-center justify-center gap-2" data-id="element-108" data-mp="3k0g88xtc3n76">
          {insidePages.map((page, i) => <button key={`dot-${i}`} type="button" onClick={() => scrollTo(i)} aria-label={`Aller à la page ${i + 1}`} aria-current={i === active} className={`h-2 rounded-full transition-[width,background-color] duration-150 ease-out ${i === active ? 'w-7 bg-tech' : 'w-2 bg-metal hover:bg-steel'}`} data-id="element-109" data-mp="3691mij81l7wf" data-mpi={i} />)}
        </div>
      </div>
    </section>;
}